import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "../pages/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Building2, ChevronDown, Calendar, Target, TrendingUp, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };

export default function CRMPerformance() {
  const { token } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kpi/crm-performance`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-12"><div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-8">No CRM data available</p>;

  const d = data;
  const projects = d.project_accountability || [];
  const weekly = d.weekly || [];
  const mask = d.masking_alert || {};
  const crmMonth = d.crm_month || {};

  const toggleProject = (p) => setExpandedProjects(prev => ({ ...prev, [p]: !prev[p] }));

  return (
    <div className="space-y-5" data-testid="crm-performance">

      {/* ── Masking Alert ── */}
      {mask.missed_customers > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200" data-testid="masking-alert">
          <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-800">Masking Alert — {d.month}</p>
            <p className="text-xs text-red-700 mt-1">
              {mask.missed_customers} customer{mask.missed_customers > 1 ? 's' : ''} with outstanding of <b>{fmt(mask.missed_amount)}</b> {mask.appears_achieved ? 'masked by over-collection from other customers' : 'not yet collected'}.
              {mask.advance_amount > 0 && <> Advance collections of <b>{fmt(mask.advance_amount)}</b> received for future months.</>}
            </p>
          </div>
        </div>
      )}

      {/* ── CRM Month Summary ── */}
      <Card className="bg-gradient-to-br from-[#0F1D32] to-[#1a2d4a] rounded-2xl border-0 text-white shadow-lg" data-testid="crm-month-hero">
        <CardContent className="p-5 sm:p-6">
          <p className="text-[10px] font-bold text-blue-300 uppercase tracking-[0.15em] mb-4">CRM Collection — {d.month}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Month Target</p><p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(crmMonth.target)}</p></div>
            <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Collected</p><p className="text-xl font-bold text-emerald-400 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(crmMonth.collected)}</p></div>
            <div><p className="text-[9px] text-slate-400 uppercase font-semibold">Achievement</p><p className={`text-xl font-bold mt-0.5 ${crmMonth.achievement_pct >= 80 ? 'text-emerald-400' : crmMonth.achievement_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`} style={{ fontFamily: 'Manrope' }}>{crmMonth.achievement_pct || 0}%</p></div>
            <div><p className="text-[9px] text-red-300 uppercase font-semibold">Missed</p><p className="text-xl font-bold text-red-400 mt-0.5" style={{ fontFamily: 'Manrope' }}>{mask.missed_customers}</p><p className="text-[9px] text-red-300/70">{fmt(mask.missed_amount)} uncollected</p></div>
            <div><p className="text-[9px] text-amber-300 uppercase font-semibold">Advance (Future)</p><p className="text-xl font-bold text-amber-400 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(crmMonth.advance)}</p></div>
            <div><p className="text-[9px] text-cyan-300 uppercase font-semibold">Recovery (Past)</p><p className="text-xl font-bold text-cyan-400 mt-0.5" style={{ fontFamily: 'Manrope' }}>{fmt(crmMonth.recovery)}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* ── 3. Weekly Collection Calendar ── */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="weekly-calendar">
        <CardHeader className="pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-blue-500" /><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Weekly Collection Calendar — {d.month}</CardTitle></div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {weekly.map((w, i) => {
              const isPast = w.end < new Date().toISOString().slice(0, 10);
              const isCurrent = w.start <= new Date().toISOString().slice(0, 10) && w.end >= new Date().toISOString().slice(0, 10);
              return (
                <div key={i} className={`p-4 rounded-xl border ${isCurrent ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200' : isPast ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Week {i + 1}</span>
                    {isCurrent && <span className="text-[8px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">CURRENT</span>}
                  </div>
                  <p className="text-xs text-slate-600 mb-2">{w.label}</p>
                  <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{fmt(w.expected)}</p>
                  <div className="mt-2 space-y-1">
                    {w.overdue > 0 && <div className="flex justify-between text-[10px]"><span className="text-red-500">Overdue</span><span className="font-semibold text-red-600">{fmt(w.overdue)}</span></div>}
                    {w.current > 0 && <div className="flex justify-between text-[10px]"><span className="text-blue-500">Due this week</span><span className="font-semibold text-blue-600">{fmt(w.current)}</span></div>}
                    {w.pipeline > 0 && <div className="flex justify-between text-[10px]"><span className="text-emerald-500">Pipeline</span><span className="font-semibold text-emerald-600">{fmt(w.pipeline)}</span></div>}
                  </div>
                  {w.top_projects?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      {w.top_projects.map(([name, val], j) => (
                        <p key={j} className="text-[9px] text-slate-500 flex justify-between"><span className="truncate mr-1">{name}</span><span className="font-semibold text-slate-700">{fmt(val)}</span></p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── 2. Customer-Level Accountability by Project ── */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm" data-testid="accountability">
        <CardHeader className="pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Customer-Level Accountability — {d.month}</CardTitle>
            <span className="text-[10px] text-red-500 font-semibold">{mask.missed_customers} missed</span>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="space-y-1.5">
            {projects.map((p, i) => (
              <div key={i}>
                <button onClick={() => toggleProject(p.project)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-semibold text-slate-800">{p.project}</span>
                    <span className="text-[9px] text-slate-400">{p.customer_count} customers</span>
                    {p.missed_count > 0 && <span className="text-[8px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full">{p.missed_count} missed</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-500">Due: {fmt(p.due)}</span>
                    <span className="text-[10px] font-bold text-red-600">Gap: {fmt(p.outstanding)}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expandedProjects[p.project] ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {expandedProjects[p.project] && (
                  <div className="mt-1 mx-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-slate-200">
                        <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Customer</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Unit</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-blue-600">Total Due</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-indigo-600">This Month</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-emerald-600">Collected</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-red-600">Outstanding</th>
                        <th className="text-center py-1.5 px-2 font-semibold text-slate-600">Status</th>
                      </tr></thead>
                      <tbody>
                        {p.customers.map((c, j) => (
                          <tr key={j} className={`border-b border-slate-100 ${c.status === 'missed' ? 'bg-red-50/50' : c.status === 'overdue' ? 'bg-amber-50/30' : ''}`}>
                            <td className="py-1.5 px-2 text-slate-800 max-w-[140px] truncate">{c.customer}</td>
                            <td className="py-1.5 px-2 text-slate-600">{c.unit}</td>
                            <td className="py-1.5 px-2 text-right text-blue-600">{fmt(c.due)}</td>
                            <td className="py-1.5 px-2 text-right text-indigo-600">{c.month_due > 0 ? fmt(c.month_due) : '-'}</td>
                            <td className="py-1.5 px-2 text-right text-emerald-600">{fmt(c.collected)}</td>
                            <td className="py-1.5 px-2 text-right font-medium text-red-600">{c.outstanding > 0 ? fmt(c.outstanding) : '-'}</td>
                            <td className="py-1.5 px-2 text-center">
                              {c.status === 'paid' ? <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">Paid</span> : c.status === 'missed' ? <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full">Missed</span> : <span className="text-[9px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">Overdue</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
